import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { History, Calendar, Filter, Clock } from 'lucide-react';

export default function BetHistoryPage() {
  const [bets, setBets] = useState([]);
  const [stats, setStats] = useState({ total_count: 0, total_amount: 0, total_won: 0, total_lost: 0 });
  const [filter, setFilter] = useState('today');

  const fetchBets = useCallback(async () => {
    try {
      if (filter === 'today') {
        const res = await api.get('/game/my-bets-today');
        setBets(res.data.bets);
        setStats({ total_count: res.data.total_count, total_amount: res.data.total_amount, total_won: res.data.total_won, total_lost: res.data.total_lost });
      } else {
        const res = await api.get('/game/my-bets');
        const b = res.data.bets;
        setBets(b);
        setStats({
          total_count: b.length,
          total_amount: b.reduce((s, x) => s + (x.amount || 0), 0),
          total_won: b.filter(x => x.status === 'won').reduce((s, x) => s + (x.winnings || 0), 0),
          total_lost: b.filter(x => x.status === 'lost').reduce((s, x) => s + (x.amount || 0), 0),
        });
      }
    } catch {}
  }, [filter]);

  useEffect(() => { fetchBets(); }, [fetchBets]);
  useEffect(() => {
    const interval = setInterval(fetchBets, 15000);
    return () => clearInterval(interval);
  }, [fetchBets]);

  return (
    <div className="min-h-screen pb-28 p-4" style={{ background: 'var(--neon-bg)' }}>
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Title */}
        <h1 className="text-xl font-black flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text)' }}>
          <History size={20} style={{ color: 'var(--neon-primary)' }} />
          Bet History
        </h1>

        {/* Stats - 2 columns, simpler */}
        <div className="grid grid-cols-2 gap-3">
          <div className="glass-card rounded-xl p-3">
            <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Total Bets</p>
            <p data-testid="history-total-count" className="text-2xl font-black" style={{ color: 'var(--neon-primary)', fontFamily: 'var(--font-heading)' }}>{stats.total_count}</p>
            <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Amount: <span style={{ color: 'var(--neon-accent)' }}>{stats.total_amount}</span></p>
          </div>
          <div className="glass-card rounded-xl p-3">
            <p className="text-xs" style={{ color: '#01FFC3' }}>Won: <span className="text-lg font-black">{stats.total_won}</span></p>
            <p className="text-xs mt-1" style={{ color: '#FF2A6D' }}>Lost: <span className="text-lg font-black">{stats.total_lost}</span></p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          <button data-testid="filter-today" onClick={() => setFilter('today')}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
            style={{ background: filter === 'today' ? 'var(--neon-primary)' : 'var(--neon-elevated)', color: filter === 'today' ? '#000' : 'var(--neon-text-secondary)' }}>
            <Calendar size={14} /> Today
          </button>
          <button data-testid="filter-all" onClick={() => setFilter('all')}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
            style={{ background: filter === 'all' ? 'var(--neon-primary)' : 'var(--neon-elevated)', color: filter === 'all' ? '#000' : 'var(--neon-text-secondary)' }}>
            <Filter size={14} /> All Time
          </button>
        </div>

        {/* Bets List - Simplified Table-like */}
        {bets.length === 0 ? (
          <div className="glass-card rounded-2xl p-10 text-center">
            <History size={36} className="mx-auto mb-3" style={{ color: 'var(--neon-text-muted)' }} />
            <p className="text-sm" style={{ color: 'var(--neon-text-muted)' }}>No bets found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-bold" style={{ color: 'var(--neon-text-muted)' }}>
              <span className="col-span-2">Number</span>
              <span className="col-span-3">Option</span>
              <span className="col-span-3">Company</span>
              <span className="col-span-2 text-right">Amount</span>
              <span className="col-span-2 text-right">Status</span>
            </div>

            {bets.map((bet, idx) => {
              const isWon = bet.status === 'won';
              const isLost = bet.status === 'lost';
              const statusColor = isWon ? '#01FFC3' : isLost ? '#FF2A6D' : '#FFD700';
              const statusText = isWon ? 'WON' : isLost ? 'LOST' : 'PLACED';
              const statusBg = isWon ? 'rgba(1,255,195,0.12)' : isLost ? 'rgba(255,42,109,0.12)' : 'rgba(255,215,0,0.12)';

              return (
                <div key={idx} data-testid={`bet-history-${idx}`}
                  className="glass-card rounded-xl p-3 grid grid-cols-12 gap-2 items-center"
                  style={{ borderLeft: `3px solid ${statusColor}` }}>
                  {/* Number */}
                  <div className="col-span-2">
                    <span className="font-black text-lg" style={{ color: 'var(--neon-primary)', fontFamily: 'var(--font-heading)' }}>
                      {bet.number}
                    </span>
                  </div>
                  {/* Option */}
                  <div className="col-span-3">
                    <p className="text-xs font-bold truncate" style={{ color: 'var(--neon-text)' }}>{bet.option_text}</p>
                    {bet.hurf_side && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded inline-block mt-0.5"
                        style={{ background: (bet.hurf_side === 'A' || bet.hurf_side === 'left') ? 'rgba(5,217,232,0.15)' : (bet.hurf_side === 'B' || bet.hurf_side === 'right') ? 'rgba(255,42,109,0.15)' : 'rgba(171,71,188,0.15)', color: (bet.hurf_side === 'A' || bet.hurf_side === 'left') ? 'var(--neon-primary)' : (bet.hurf_side === 'B' || bet.hurf_side === 'right') ? 'var(--neon-secondary)' : '#AB47BC' }}>
                        {bet.number}{(bet.hurf_side === 'A' || bet.hurf_side === 'left') ? 'A' : (bet.hurf_side === 'B' || bet.hurf_side === 'right') ? 'B' : 'AB'}
                      </span>
                    )}
                  </div>
                  {/* Company */}
                  <div className="col-span-3">
                    <p className="text-xs truncate" style={{ color: 'var(--neon-text-secondary)' }}>{bet.company_name}</p>
                    <p className="text-[10px] flex items-center gap-0.5" style={{ color: 'var(--neon-text-muted)' }}>
                      <Clock size={8} /> {new Date(bet.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {/* Amount */}
                  <div className="col-span-2 text-right">
                    <p className="font-black text-sm" style={{ color: 'var(--neon-accent)', fontFamily: 'var(--font-heading)' }}>{bet.amount}</p>
                    {isWon && bet.winnings && (
                      <p className="text-[10px] font-bold" style={{ color: '#01FFC3' }}>+{bet.winnings}</p>
                    )}
                  </div>
                  {/* Status */}
                  <div className="col-span-2 text-right">
                    <span className="text-[10px] px-2 py-1 rounded-full font-black inline-block"
                      style={{ background: statusBg, color: statusColor }}>
                      {statusText}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
